import { List as ImmutableList, Record, type RecordOf } from 'immutable';
import type { UnknownAction } from 'redux';

import { getLogger } from '../../utils/core';
import { hasAsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { coreActions } from '../core-actions';
import * as undoActions from './undoActions';
import type { Undo, UndoDeferToken } from './undoTypes';

const log = getLogger('data/undo/undoReducer.ts');
export const UNDO_REDUCER_KEY = 'undoStore';

export interface UndoStoreProps {
  // Every producer builds entries with mkUndo, so the stack holds records, not bare props.
  undoStack: ImmutableList<Undo>;
}

export type UndoStore = RecordOf<UndoStoreProps>;

export const mkUndoStore = Record<UndoStoreProps>({
  undoStack: ImmutableList<Undo>(),
});

const maxUndoStackSize = 1;

// The payload type is asserted rather than checked: every producer of these actions is in-tree.
const hasObjectPayload = <T extends object = object>(action: UnknownAction): action is UnknownAction & { payload: T } =>
  typeof action.payload === 'object' && action.payload !== null;

export const reducer = (state: UndoStore = mkUndoStore(), action: UnknownAction): UndoStore => {
  switch (action.type) {
    case coreActions.SANITIZE:
      return state.delete('undoStack');

    case undoActions.addUndo.toString():
      if (!hasObjectPayload<Undo>(action)) return state;
      log.log('addUndoAction...');
      return state.set('undoStack', state.undoStack.takeLast(maxUndoStackSize - 1).push(action.payload));

    case undoActions.undo.toString():
      if (!hasObjectPayload<Undo>(action) || !hasAsyncDispatch(action)) return state;
      log.log('dispatchPostponedActions...');
      action.payload.actions?.forEach((undoAction) => action.asyncDispatch(undoAction));
      return state.set(
        'undoStack',
        state.undoStack.filterNot((undo) => undo === action.payload),
      );

    case undoActions.deleteUndo.toString():
      if (!hasObjectPayload<Undo>(action)) return state;
      log.log('deleteUndoAction...');
      return state.set(
        'undoStack',
        state.undoStack.filterNot((undo) => undo === action.payload),
      );

    case undoActions.revealUndo.toString():
      if (!hasObjectPayload<UndoDeferToken>(action)) return state;
      // Matching on the token keeps an operation from revealing a different operation's undo while
      // that one's writes are still in flight. A later unrelated undo may have replaced the deferred
      // entry, in which case nothing matches and the completed operation's undo is simply gone.
      log.log('revealUndoAction...');
      return state.set(
        'undoStack',
        state.undoStack.map((undoEntry) =>
          undoEntry.deferToast && undoEntry.deferToken === action.payload
            ? undoEntry.set('deferToast', false)
            : undoEntry,
        ),
      );

    default:
      return state;
  }
};

export default reducer;
